/*
 * The StepEmailTemplate component is the step in reseller SO flow enabling email template overriding, and SO sending.
 * 
 * This takes the following props:
 *   auth                     - authentication object
 *   customer                 - customer for which this SO is being created
 *   history                  - for navigation to non-SO flow.  TODO: refactor when moving to react router 6, consider removing functionality
 *   user                     - user for which this SO is being created
 *   project                  - project for which this SO is being created
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';

import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper, TextField, Typography } from '@mui/material';
import AppBindStyles from '../../AppBindStyles';

import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';

const abEnvironment = new AbEnvironment();
const StepEmailTemplateQuery = graphql`
query StepEmailTemplateQuery($accountId: UUID!, $projectId: UUID) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountName
    logo
    contactFirstName
    contactLastName
    contactEmail
    accountType
    initialBalanceAmount
    autoTopupActive
    rAutoTopupActive
    autoTopupAmount
    rAutoTopupAmount
    autoTopupThreshhold
    rAutoTopupThreshold
    maxMonthlyBudget
    rMaxMonthlyBudget
    maxCharge
    rMaxCharge
    preApproveSubs
    rPreApproveSubs
    budgetApprovedCount
    projectTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        walletTByCustomerWalletId {
          walletId
          initialBalanceAmount
          autoTopupActive
          rAutoTopupActive
          autoTopupAmount
          rAutoTopupAmount
          autoTopupThreshold
          rAutoTopupThreshold
          maxMonthlyBudget
          rMaxMonthlyBudget
        }
        walletTByResellerWalletId {
          walletId
          initialBalanceAmount
          autoTopupActive
          rAutoTopupActive
          autoTopupAmount
          rAutoTopupAmount
          autoTopupThreshold
          rAutoTopupThreshold
          maxMonthlyBudget
          rMaxMonthlyBudget
        }
      }
    }
    userTsByAccountId {
      totalCount
      nodes {
        userId
        userName
        userRole
        lastName
        firstName
        email
        userArole
        userConfirmDate
        termsAcceptDate
        auth0Uid
      }
    }
    accountTByParentId {
    	accountName
      accountId
      contactFirstName
      contactLastName
      contactEmail
      accountStatus
      personaStatus
    }
    paymentSourceTsByAccountId {
      nodes {
        psType
        psStatus
      }
    }
    customerAppTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        customerAppId
        appId
        customerAccountId
        appStatus
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        paymentOrder
        passThroughFees
        rPaymentOrder
        rPassThroughFees
        rAppMarkupPercentage
        bcId
        bindCardTByBcId {
          bcStatus
        }
        sharedEmail
        pwd
        showSubscription
        passThroughFees
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
    customerServiceTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        soEntered
        soAccepted
        serviceStatus
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  entryField: {
    paddingLeft: 16,
    paddingTop: 24,
  },
  button: {
    marginTop: 24,
    marginRight: '24px',
    paddingTop: '8px',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '8px',
  },
  imageContainer: {
    height: 400,
    border: '1px solid black'
  },
  title: {
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: theme.palette.secondary.main,
    textAlign: 'left',
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px"
  },
  titleLine: {
    color : "white", 
    fontSize: "21px",
    fontWeight: "500",
    paddingLeft: "16px",
    paddingTop: "16px",
    paddingBottom: "8px"
  },
  appgrid: {
    marginBottom: 12,
  },
  noLimitText: {
    width: 156
  }
});

class StepEmailTemplate extends React.Component {
  
  state = {
    emailSubject: null,
    textBody: null,
    showSpinner: false,
    showSkipSpinner: false
  }

  textBodyDefault = null;

  showSpinner() {
    return this.state.showSpinner;
  }

  showSkipSpinner() {
    return this.state.showSkipSpinner;
  }

  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    return false;
  }

  isNextDisabled() {
    if (this.props.activeStep === this.props.assignedStep) {
      if (this.state.userError === true) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  isSendEmailDisabled = () => {
    if (this.state.textBody !== null) {
      return !this.props.auth.isEmailContentAllowed(this.state.textBody);
    } else {
      return false;
    }
  }

  /**
   * Call API to auto approve the order.
   * 
   * @param {*} sendParameters - JSON contained parameters for this method, of this form:
   * {
   *   pAccountId:           <reseller account id>,
   *   accountId:            <customer account id>,
   *   accountName:          <customer account name>,
   *   projectId:            <project id (null if this is account based)>,
   *   userToEmail:          <user object - the user to send the order to>,
   *   emailSubject:         <subject of the email>,
   *   preApproveSubs:       <from project if project based, otherwise from account>,
   *   rPreApproveSubs:      <from project if project based, otherwise from account>,
   *   budgetChanged:        <indicates if topup values have changed>,
   *   servicesAdded:        <indicates if services have been added>,
   *   budgetApprovedCount:  <approved counter>,
   *   customerPaidCount:    <customer paid counter>,
   *   autoTopupAmount:      <from wallet if project based, otherwise from account>,
   *   initialBalanceAmount: <from wallet if project based, otherwise from account>,
   *   rAutoTopupAmount:     <from wallet if project based, otherwise from account>,
   *   rAutoTopupThreshold:  <from wallet if project based, otherwise from account>,
   *   rMaxMonthlyBudget:    <from wallet if project based, otherwise from account>,
   *   rMaxCharge:           <from wallet if project based, otherwise from account>
   * }
   * 
   */
  handleSkip = (sendParameters) => {
    const { callPartnerApi, logError } = this.props.auth;

    this.setState({ showSkipSpinner: true });

    let aPjwt  = abEnvironment.getPjwt();
    let userId = abEnvironment.getUserAttribute('u_id');

    //update latest budget approved 
    Promise.resolve()  
    .then(() => {
      let payload;
      if (sendParameters.projectId == null) {
        payload = {
          operation: "confirmSubscriptionOrder",
          params: {
            pJwt:                 aPjwt,
            accountId:            sendParameters.accountId,
            accountName:          sendParameters.accountName,
            pAccountId:           sendParameters.pAccountId,
            latestBudgetApproved: 1,
            rAutoTopupAmount:     sendParameters.rAutoTopupAmount,
            rAutoTopupThreshold:  sendParameters.rAutoTopupThreshold,
            rMaxMonthlyBudget:    sendParameters.rMaxMonthlyBudget,
            rMaxCharge:           sendParameters.rMaxCharge,
            rPreApproveSubs:      sendParameters.rPreApproveSubs,
            budgetApprovedCount:  sendParameters.budgetApprovedCount + 1,
            sendNotification:     false,
            confirmUserId:        userId   
          }
        };
      } else {
        payload = {
          operation: "confirmProjectSubscriptionOrder",
          params: {
            pJwt:                 aPjwt,
            accountId:            sendParameters.accountId,
            accountName:          sendParameters.accountName,
            projectId:            sendParameters.projectId,
            pAccountId:           sendParameters.pAccountId,
            latestBudgetApproved: 1,
            rAutoTopupAmount:     sendParameters.rAutoTopupAmount,
            rAutoTopupThreshold:  sendParameters.rAutoTopupThreshold,
            rMaxMonthlyBudget:    sendParameters.rMaxMonthlyBudget,
            rMaxCharge:           sendParameters.rMaxCharge,
            rPreApproveSubs:      sendParameters.rPreApproveSubs,
            budgetApprovedCount:  sendParameters.budgetApprovedCount + 1,
            sendNotification:     false,
            confirmUserId:        userId   
          }
        };
      }

      return callPartnerApi('appbind/generic',
                    'POST',
                    payload);

    }).then(resp => {
       return resp.json();
    }).then((jresp) => {
      this.setState({ showSkipSpinner: false });
      //go to subscriptions tab for customer
      localStorage.setItem('customerTabVal', 1);  //positions user on subscriptions tab
      this.props.history.replace('/customer?accountId=' + this.props.customer.accountId);
  
    }).catch(err => {
      logError('Exception in StepEmailTemplate.js: ' + err);
    });
  }

  /**
   * Call APIs to send or auto approve the order.
   * 
   * @param {*} sendParameters - JSON contained parameters for this method, of this form:
   * {
   *   pAccountId:           <reseller account id>,
   *   accountId:            <customer account id>,
   *   accountName:          <customer account name>,
   *   projectId:            <project id (null if this is account based)>,
   *   userToEmail:          <user object - the user to send the order to>,
   *   emailSubject:         <subject of the email>,
   *   preApproveSubs:       <from project if project based, otherwise from account>,
   *   rPreApproveSubs:      <from project if project based, otherwise from account>,
   *   budgetChanged:        <indicates if topup values have changed>,
   *   servicesAdded:        <indicates if services have been added>,
   *   budgetApprovedCount:  <approved counter>,
   *   customerPaidCount:    <customer paid counter>,
   *   autoTopupAmount:      <from wallet if project based, otherwise from account>,
   *   initialBalanceAmount: <from wallet if project based, otherwise from account>,
   *   rAutoTopupAmount:     <from wallet if project based, otherwise from account>,
   *   rAutoTopupThreshold:  <from wallet if project based, otherwise from account>,
   *   rMaxMonthlyBudget:    <from wallet if project based, otherwise from account>,
   *   rMaxCharge:           <from wallet if project based, otherwise from account>
   * }
   */
  handleSend = (sendParameters) => {
    const { callPartnerApi } = this.props.auth;

    this.setState({ showSpinner: true });

    let autoApprove = false;
    if (sendParameters.preApproveSubs === 1 && sendParameters.budgetChanged === false && sendParameters.servicesAdded === false) {
      autoApprove = true;
    }

    let aPjwt      = abEnvironment.getPjwt();
    let pAccountId = abEnvironment.getUserAttribute('a_id');
    let userId     = abEnvironment.getUserAttribute('u_id');

    //for the emailBody, replace any new line with appropriate html
    let theTextBody = this.state.textBody;
    if (theTextBody == null) {
      theTextBody = this.textBodyDefault;
    }

    const htmlBody = theTextBody.split("\n").join("<br/>");
    const newTextBody = theTextBody.split("\n").join("\\n");
 
    let payload;
    if (sendParameters.projectId == null) {
      payload = {
        operation: "sendSOToCustomer",
        params: {
          pJwt:           aPjwt,
          pAccountId:     pAccountId,
          accountId:      sendParameters.accountId,
          abUserId:       sendParameters.userToEmail.userId,
          subject:        (this.state.emailSubject) ? this.state.emailSubject : sendParameters.emailSubject,
          htmlBody:       htmlBody,
          textBody:       newTextBody,
          preApproveSubs: sendParameters.preApproveSubs,
          budgetChanged:  sendParameters.budgetChanged,
          servicesAdded:  sendParameters.servicesAdded
        }
      };
    } else {
      payload = {
        operation: "sendProjectSOToCustomer",
        params: {
          pJwt:           aPjwt,
          pAccountId:     pAccountId,
          accountId:      sendParameters.accountId,
          abUserId:       sendParameters.userToEmail.userId,
          subject:        (this.state.emailSubject) ? this.state.emailSubject : sendParameters.emailSubject,
          htmlBody:       htmlBody,
          textBody:       newTextBody,
          preApproveSubs: sendParameters.preApproveSubs,
          budgetChanged:  sendParameters.budgetChanged,
          servicesAdded:  sendParameters.servicesAdded,
          projectId:      sendParameters.projectId
        }
      };  
    }

    callPartnerApi('appbind/generic',
                    'POST',
                    payload
    )
    .then(response => {
      if (autoApprove) {
        //call backend API to confirm, etc
        let payload;
        if (sendParameters.projectId == null) {
          payload = {
            operation: "confirmSubscriptionOrder",
            params: {
              pJwt:                 aPjwt,
              accountId:            sendParameters.accountId,
              accountName:          sendParameters.accountName,
              pAccountId:           sendParameters.pAccountId,
              latestBudgetApproved: 1,
              rAutoTopupAmount:     sendParameters.rAutoTopupAmount,
              rAutoTopupThreshold:  sendParameters.rAutoTopupThreshold,
              rMaxMonthlyBudget:    sendParameters.rMaxMonthlyBudget,
              rMaxCharge:           sendParameters.rMaxCharge,
              rPreApproveSubs:      sendParameters.rPreApproveSubs,
              budgetApprovedCount:  sendParameters.budgetApprovedCount + 1,
              sendNotification:     false,
              confirmUserId:        userId   
            }
          };
        } else {
          payload = {
            operation: "confirmProjectSubscriptionOrder",
            params: {
              pJwt:                 aPjwt,
              accountId:            sendParameters.accountId,
              accountName:          sendParameters.accountName,
              projectId:            sendParameters.projectId,
              pAccountId:           sendParameters.pAccountId,
              latestBudgetApproved: 1,
              rAutoTopupAmount:     sendParameters.rAutoTopupAmount,
              rAutoTopupThreshold:  sendParameters.rAutoTopupThreshold,
              rMaxMonthlyBudget:    sendParameters.rMaxMonthlyBudget,
              rMaxCharge:           sendParameters.rMaxCharge,
              rPreApproveSubs:      sendParameters.rPreApproveSubs,
              budgetApprovedCount:  sendParameters.budgetApprovedCount + 1,
              sendNotification:     false,
              confirmUserId:        userId   
            }
          };
        }

        return callPartnerApi('appbind/generic',
                        'POST',
                        payload)
      } else {
        return null;
      }

    }).then(resp => { 
      this.setState({ showSpinner: false });
      //go to subscriptions tab for customer
      localStorage.setItem('customerTabVal', 1);  //positions user on subscriptions tab
      this.props.history.replace('/customer?accountId=' + this.props.customer.accountId);
    })
    .catch(error => {
      this.setState({ showSpinner: false }); 
    });
  }

  handleSave = () => {
    //sort of a no-op 
    localStorage.setItem('customerTabVal', 1);  //positions user on subscriptions tab
    this.props.history.replace('/customer?accountId=' + this.props.customer.accountId);
  }

  handleSaveAndVerify = (personaStatus) => {
    //sort of a no-op 
    if (personaStatus) {
      localStorage.setItem('customerTabVal', 1);  //positions user on subscriptions tab
      this.props.history.replace('/customer?accountId=' + this.props.customer.accountId);
    } else {
      this.props.history.replace('/home');
    }
  }

  handleNext() {
    //cannot validate since user not logged in or not trying to add new.
    this.props.handleNext();
  }

  getTextBodyForRender(initVal) {
    if (this.state.textBody != null) {
      return this.state.textBody;
    }

    return initVal;
  }

  render() {
    const { logError } = this.props.auth;
    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }
  
    let acctId = this.props.customer.accountId;

    // Was a project passed in?  If so, the subscription is for a project.
    // If not, legacy case.
    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={StepEmailTemplateQuery}
        variables={{accountId: acctId, projectId: projectId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {

            let theAccount = props.accountTByAccountId;

            // Project if relevant
            let theProject = null;
            if (theAccount.projectTsByCustomerAccountId.totalCount > 0) {
              theProject = theAccount.projectTsByCustomerAccountId.nodes[0];
            }

            /*
             * This is the last step, so this should always be active (not display only), but for consistency,
             * we're still wrapping in this check.
             */
            if (!displayOnly) {
              //
              // Set up the email subject line.  This may be changed to a notifiation message.
              //
              let emailSubject = "Approve your project's subscriptions";

              //
              // Find user we are sending the order to
              //
              let userToEmail = null;
              if (theAccount.userTsByAccountId.totalCount > 0) {                  
                let users = theAccount.userTsByAccountId.nodes;
                for (let i = 0; i < users.length; i++) {
                  let user = JSON.parse(JSON.stringify( users[i]));
                  if (user.userId === this.props.user.userId ) {
                    userToEmail = user;
                    break;
                  }
                }  
              }

              //
              // Determine if services were added
              //
              let servicesAdded = false;
              for (let i = 0; i < theAccount.customerServiceTsByCustomerAccountId.nodes.length; i++) {
                let service = theAccount.customerServiceTsByCustomerAccountId.nodes[i];
                //exclude cancelled subs
                if (service.serviceStatus === 0 && service.soAccepted === 0 ) {
                  servicesAdded = true;
                }
              }

              // ----------------------------------------------
              // Budget changes flags
              // ----------------------------------------------
              let budgetChanged = false; // Are auto topup values or limits changed?  This is used at send time to determine where to redirect the user
              let autoTopupAlreadyActive = false;  // Is auto topup already active?

              // Get values from account (pre-setting, but will be overridden if this is a project)
              let preApproveSubs       = theAccount.preApproveSubs;
              let rPreApproveSubs      = theAccount.rPreApproveSubs;
              let budgetApprovedCount  = theAccount.budgetApprovedCount;
              let initialBalanceAmount = theAccount.initialBalanceAmount;
              let autoTopupActive      = theAccount.autoTopupActive;
              let rAutoTopupActive     = theAccount.rAutoTopupActive;
              let autoTopupAmount      = theAccount.autoTopupAmount;
              let rAutoTopupAmount     = theAccount.rAutoTopupAmount;
              let autoTopupThreshold   = theAccount.autoTopupThreshhold;
              let rAutoTopupThreshold  = theAccount.rAutoTopupThreshold;
              let maxMonthlyBudget     = theAccount.maxMonthlyBudget;
              let rMaxMonthlyBudget    = theAccount.rMaxMonthlyBudget;
              let rMaxCharge           = theAccount.rMaxCharge;

              if (theProject != null) {
                preApproveSubs      = theProject.preApproveSubs;
                rPreApproveSubs     = theProject.rPreApproveSubs;
                budgetApprovedCount = theProject.budgetApprovedCount;

                let theCustomerWallet = theProject.walletTByCustomerWalletId;
                if (theCustomerWallet != null) {
                  initialBalanceAmount = theCustomerWallet.initialBalanceAmount;
                  autoTopupActive      = theCustomerWallet.autoTopupActive;
                  rAutoTopupActive     = theCustomerWallet.rAutoTopupActive;
                  autoTopupAmount      = theCustomerWallet.autoTopupAmount;
                  rAutoTopupAmount     = theCustomerWallet.rAutoTopupAmount;
                  autoTopupThreshold   = theCustomerWallet.autoTopupThreshold;
                  rAutoTopupThreshold  = theCustomerWallet.rAutoTopupThreshold;
                  maxMonthlyBudget     = theCustomerWallet.maxMonthlyBudget;
                  rMaxMonthlyBudget    = theCustomerWallet.rMaxMonthlyBudget;
                  rMaxCharge           = theCustomerWallet.rMaxCharge;
                }
              }

              let customerFundsRequested = false; // Were customer funds requested (one-off fund addition)?
              let autoTopupTurnedOn      = false; // Was auto-topup turned on in this SO?
              let autoTopupTurnedOff     = false; // Was auto-topup turned off in this SO?
              let autoTopupChanged       = false; // Was an auto topup value changed in this SO?
              let autoTopupIncreased     = false; // Was an auto topup value increased?
              let autoTopupDecreased     = false; // Was an auto topup value increased?

              let limitChanged      = false; // Was a monthly limit changed in this SO?
              let limitAdded        = false; // Was a monthly limit added?
              let limitRemoved      = false; // Was a monthly limit removed?
              let limitIncreased    = false; // Was a monthly limit increased?
              let limitDecreased    = false; // Was a monthly limit decreased?

              // Determine what changes are being made in this SO
              // - Is there a funding change?
              //   - One-off funding?
              //   - Auto-topup?
              // - Is there a change to spend controls (monthly limit)
              //
              //console.log('autoTopupActive: ' + autoTopupActive + ', rAutoTopupActive: ' + rAutoTopupActive + ', autoTopupAmount: ' + autoTopupAmount + ', rAutoTopupAmount: ' + rAutoTopupAmount + ', autoTopupThreshold: ' + autoTopupThreshold + ', rAutoTopupThreshold: ' + rAutoTopupThreshold + ', maxMonthlyBudget: ' + maxMonthlyBudget + ', rMaxMonthlyBudget: ' + rMaxMonthlyBudget + ', initialBalanceAmount: ' + initialBalanceAmount);
              if (autoTopupActive === 1 && budgetApprovedCount > 0) {
                autoTopupAlreadyActive = true;
                if (rAutoTopupActive !== 1) {
                  autoTopupTurnedOff = true;
                } else {
                  if (Number(rAutoTopupAmount) > Number(autoTopupAmount) || Number(rAutoTopupThreshold) > Number(autoTopupThreshold)) {
                    autoTopupIncreased = true;
                  } else if ((Number(rAutoTopupAmount) < Number(autoTopupAmount)) || (Number(rAutoTopupThreshold) < Number(autoTopupThreshold))) {
                    autoTopupDecreased = true;
                  }
                }
              } else {
                if (rAutoTopupActive === 1) {
                  autoTopupTurnedOn = true;
                }
              }

              if (autoTopupTurnedOn === true || autoTopupTurnedOff === true || autoTopupIncreased === true || autoTopupDecreased === true) {
                autoTopupChanged = true;
              }

              if (maxMonthlyBudget != null) {
                if (rMaxMonthlyBudget == null) {
                  limitRemoved = true;
                } else if (Number(rMaxMonthlyBudget) < Number(maxMonthlyBudget)) {
                  limitDecreased = true;
                } else if (Number(rMaxMonthlyBudget) > Number(maxMonthlyBudget)) {
                  limitIncreased = true;
                }
              } else {
                if (rMaxMonthlyBudget != null) {
                  limitAdded = true;
                }
              }

              if (limitAdded === true || limitRemoved === true || limitIncreased === true || limitDecreased === true) {
                limitChanged = true;
              }

              if (initialBalanceAmount > 0) {
                customerFundsRequested = true;
              }

              if (autoTopupChanged === true || limitChanged === true) {
                budgetChanged = true;
              }

              let subsequentSO = false;
              if (budgetApprovedCount > 0) {
                subsequentSO = true;
              }
                
              //
              // The approach here was to include getbincardsunattachedfordisplay
              // in the QueryRenderer lookup.  However, let's shift to using the same
              // approach that is used in the StepSubscriptions step, which will use
              // the customerAppT and bindCardT lookups to get counts for current
              // and pending subscriptions, further categorized by customer pay (as
              // we're asking for auth here) and access request.
              // 

              // Set up an array of all active and pending subscriptions (including suspended)
              let allSubscriptions = [];         // All non-cancelled
              let pendingExpenseToCustomer = []; // Asking for auth for these
              let changedExpenseToCustomer = []; // Noting that these have changed
              let pendingExpenseToReseller = []; // Future use
              let pendingExpense = [];           // Consolidate all new paid apps into one alphabatized list
              let changedExpenseToReseller = []; // Future use
              let pendingAccessRequest = [];     // We'll note that these will be asked about
              let pendingSignUp = [];            // We'll note that these will be asked about
              let customerPaidCount = 0;
              let resellerPaidCount = 0;
              let accessRequestCount = 0;
              let signUpCount = 0; // paymentOrder 5 - I'll sign up.

              // Get all non-cancelled subscriptions
              
              let debugString = null; // Set to null for no message trace.
              if (debugString != null) {
                debugString = debugString + "There are " + theAccount.customerAppTsByCustomerAccountId.nodes.length + " total subscriptions.";
              }
              
              for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
                let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
                //exclude cancelled subs
                if (sub.appStatus === 2) {
                  continue;
                }

                allSubscriptions.push(sub);
              }

              // Sort the subscriptions by app name
              if (allSubscriptions.length > 1) {
                allSubscriptions.sort(function(a, b) {
                  if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                  if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                  return 0;
                });
              }

              // Categorize
              for (let i = 0; i < allSubscriptions.length; i++) {
                let sub = allSubscriptions[i];
                if (sub.paymentOrder === 0 || sub.paymentOrder === 1) {
                  customerPaidCount++;
                  if (sub.bindCardTByBcId.bcStatus === -1) {
                    pendingExpenseToCustomer.push(sub);
                    pendingExpense.push(sub);
                  } else {
                    if (sub.rPassThroughFees !== null || sub.rAppMarkupPercentage !== null || sub.rPaymentOrder != null) {
                      changedExpenseToCustomer.push(sub);
                    }
                  }
                } else if (sub.paymentOrder === 2) {
                  resellerPaidCount++;
                  if (sub.bindCardTByBcId.bcStatus === -1) {
                    pendingExpenseToReseller.push(sub);
                    pendingExpense.push(sub);
                  } else {
                    if (sub.rPassThroughFees !== null || sub.rAppMarkupPercentage !== null || sub.rPaymentOrder != null) {
                      changedExpenseToReseller.push(sub);
                    }
                  }
                } else if (sub.paymentOrder === 5) {
                  signUpCount++;
                  if (sub.pwd == null) {
                    pendingSignUp.push(sub);
                  }
                } else if (sub.paymentOrder === 4 || sub.bindCardTByBcId.bcStatus === 4) {
                  accessRequestCount++;
                  if (sub.pwd == null) {
                    pendingAccessRequest.push(sub);
                  }
                }
              }

              //
              // Setup added app information
              // 
              if (debugString != null) {
                debugString = debugString + "\nThere are " + allSubscriptions.length + " subscriptions.";
                debugString = debugString + "\nThere are " + pendingExpenseToCustomer.length + " pending expense to customer.";
                debugString = debugString + "\nThere are " + changedExpenseToCustomer.length + " changed expense to customer.";
                debugString = debugString + "\nThere are " + pendingExpenseToReseller.length + " pending expense to reseller.";
                debugString = debugString + "\nThere are " + changedExpenseToReseller.length + " changed expense to reseller.";
                debugString = debugString + "\nThere are " + pendingAccessRequest.length + " pending access requests.";
                debugString = debugString + "\nThere are " + pendingSignUp.length + " pending sign ups.";
              }

              // All expensed pending apps (to customer or to reseller)
              let addedPayAppString = "";
              for (let i=0; i<pendingExpense.length; i++) {
                let sub = pendingExpense[i];
                if (i === 0) {
                  addedPayAppString = sub.appTByAppId.appName;
                } else {
                  if (i === (pendingExpense.length - 1)) {
                    if (i > 1) {
                      addedPayAppString = addedPayAppString + ', and ' + sub.appTByAppId.appName;
                    } else {
                      addedPayAppString = addedPayAppString + ' and ' + sub.appTByAppId.appName;
                    }
                  } else {
                    addedPayAppString = addedPayAppString + ', ' + sub.appTByAppId.appName;
                  }
                }
              }
              if (debugString != null) {
                debugString = debugString + "\nAdded paid apps: " + addedPayAppString;
              }

              // Customer paid pending apps
              let addedCustomerPayAppString = "";
              for (let i=0; i<pendingExpenseToCustomer.length; i++) {
                let sub = pendingExpenseToCustomer[i];
                if (i === 0) {
                  addedCustomerPayAppString = sub.appTByAppId.appName;
                } else {
                  if (i === (pendingExpenseToCustomer.length - 1)) {
                    if (i > 1) {
                      addedCustomerPayAppString = addedCustomerPayAppString + ', and ' + sub.appTByAppId.appName;
                    } else {
                      addedCustomerPayAppString = addedCustomerPayAppString + ' and ' + sub.appTByAppId.appName;
                    }
                  } else {
                    addedCustomerPayAppString = addedCustomerPayAppString + ', ' + sub.appTByAppId.appName;
                  }
                }
              }
              if (debugString != null) {
                debugString = debugString + "\nAdded customer pay apps: " + addedCustomerPayAppString;
              }

              // Customer paid changed apps
              let changedCustomerPayAppString = "";
              for (let i=0; i<changedExpenseToCustomer.length; i++) {
                let sub = changedExpenseToCustomer[i];
                if (i === 0) {
                  changedCustomerPayAppString = sub.appTByAppId.appName;
                } else {
                  if (i === (changedExpenseToCustomer.length - 1)) {
                    if (i > 1) {
                      changedCustomerPayAppString = changedCustomerPayAppString + ', and ' + sub.appTByAppId.appName;
                    } else {
                      changedCustomerPayAppString = changedCustomerPayAppString + ' and ' + sub.appTByAppId.appName;
                    }
                  } else {
                    changedCustomerPayAppString = changedCustomerPayAppString + ', ' + sub.appTByAppId.appName;
                  }
                }
              }
              if (debugString != null) {
                debugString = debugString + "\nChanged customer pay apps: " + changedCustomerPayAppString;
              }

              // Customer access request apps
              let addedCustomerRequestAppString = "";
              for (let i=0; i<pendingAccessRequest.length; i++) {
                let sub = pendingAccessRequest[i];
                if (i === 0) {
                  addedCustomerRequestAppString = sub.appTByAppId.appName;
                } else {
                  if (i === (pendingAccessRequest.length - 1)) {
                    if (i > 1) {
                      addedCustomerRequestAppString = addedCustomerRequestAppString + ', and ' + sub.appTByAppId.appName;
                    } else {
                      addedCustomerRequestAppString = addedCustomerRequestAppString + ' and ' + sub.appTByAppId.appName;
                    }
                  } else {
                    addedCustomerRequestAppString = addedCustomerRequestAppString + ', ' + sub.appTByAppId.appName;
                  }
                }
              }
              if (debugString != null) {
                debugString = debugString + "\nAccess requested for apps: " + addedCustomerRequestAppString;
              }

              // Sign ups
              let addedSignUpString = "";
              for (let i=0; i<pendingSignUp.length; i++) {
                let sub = pendingSignUp[i];
                if (i === 0) {
                  addedSignUpString = sub.appTByAppId.appName;
                } else {
                  if (i === (pendingSignUp.length - 1)) {
                    if (i > 1) {
                      addedSignUpString = addedSignUpString + ', and ' + sub.appTByAppId.appName;
                    } else {
                      addedSignUpString = addedSignUpString + ' and ' + sub.appTByAppId.appName;
                    }
                  } else {
                    addedSignUpString = addedSignUpString + ', ' + sub.appTByAppId.appName;
                  }
                }
              }
              if (debugString != null) {
                debugString = debugString + "\nSign up for apps: " + addedSignUpString;
              }

              // Derived values
              let customerPaySubscriptions = false;
              if (customerPaidCount > 0) {
                customerPaySubscriptions = true;
              }
              if (pendingExpenseToCustomer.length > 0 || changedExpenseToCustomer.length > 0) {
                customerPaySubscriptions = true;
              }

              let newCount = pendingExpenseToCustomer.length + pendingExpenseToReseller.length + pendingAccessRequest.length;
              let existingCount = 0;
              existingCount = customerPaidCount + resellerPaidCount + accessRequestCount - newCount;
              if (existingCount < 0) {
                existingCount = 0;
              }
              
              // - Is there already a customer funding source on file?
              let customerSourceOnFile = false;
              let sources = theAccount.paymentSourceTsByAccountId.nodes;
              if (sources != null) {
                for (let i = 0; i < sources.length; i++) {
                  let aSource = sources[i];
                  if (aSource.psStatus === 0 && (aSource.psType === 0 || aSource.psType === 1)) {
                    customerSourceOnFile = true;
                  }
                }
              }

              // - Debug for increases (set used to be here)
              if (debugString != null) {
                if (autoTopupIncreased === true) {
                  debugString = debugString + "\nAuto-topup increased";
                } else if (autoTopupDecreased === true) {
                  debugString = debugString + "\nAuto-topup decreased";
                } else {
                  debugString = debugString + "\nAuto-topup not changed";
                }

                if (customerFundsRequested === true) {
                  debugString = debugString + "\nFunds requested";
                } else {
                  debugString = debugString + "\nFunds not requested";
                }
              }

              // We only need to construct a default email body if the user hasn't
              // started changing the message.  If they are changing the message,
              // we use their text.
              let emailTextBody = this.state.textBody;
              if (emailTextBody == null || emailTextBody === '') {

                // Now set the body text.
                let needDefaultMessage = true;

                // Salutation
                emailTextBody = this.props.user.firstName + ",";

                //
                // Based on what we know, construct a reasonable message.
                //
                if (preApproveSubs === 1 && budgetChanged === false && servicesAdded === false) {
                  // Qualifies for pre-approval
                  // - preApproval is on
                  // - budget is not being changed
                  // - no services are being added
                  //
                  emailSubject = "New subscription notification";
                  emailTextBody = emailTextBody + "\n\nWe've added subscriptions for " + addedCustomerPayAppString + " to your project.  You don't need to take any actions at this time.";
                  emailTextBody = emailTextBody + "\n\n- " + abEnvironment.getUserAttribute('a_accountName');
                } else {
                  //
                  // Setup sections for:
                  // - new or changed subscriptions
                  //   - expense to customer
                  //   - expense to reseller
                  //   - access request
                  // - new or changed budget
                  // - new services
                  //

                  //
                  // --- Subscriptions ---
                  //

                  // New paid subscriptions (expense to reseller or expense to customer)
                  if (addedPayAppString !== '') {
                    if (existingCount === 0) {
                      emailTextBody = emailTextBody + "\n\nWe'd like to purchase and manage " + addedPayAppString + " for your project.";
                    } else {
                      emailTextBody = emailTextBody + "\n\nWe'd like to purchase and add " + addedPayAppString + " to the subscriptions we're managing for your project.";
                    }
                    needDefaultMessage = false;
                  }

                  // Changed subscriptions (customer pay)
                  if (changedCustomerPayAppString !== '') {
                    if (needDefaultMessage === true) {
                      emailTextBody = emailTextBody + "\n\nPlease review terms changes for ";
                      needDefaultMessage = false;
                    } else {
                      emailTextBody = emailTextBody + "  Please also review terms changes for ";
                    }
                    emailTextBody = emailTextBody + changedCustomerPayAppString + ".";
                  }

                  if (preApproveSubs === 0 && rPreApproveSubs === 1) {
                    if (needDefaultMessage === true) {
                      emailTextBody = emailTextBody + "\n\nWe would like your approval to add subscriptions to your project as needed.";
                      needDefaultMessage = false;
                    } else {
                      emailTextBody = emailTextBody + "\n\nWe would like your approval to add subscriptions to your project as needed.";
                    }
                  }    

                  // New subscriptions (request access)
                  if (addedCustomerRequestAppString !== '') {
                    if (needDefaultMessage === true) {
                      emailTextBody = emailTextBody + "\n\nWe need access to ";
                    } else {
                      emailTextBody = emailTextBody + "\n\nWe also need access to ";
                    }
                    emailTextBody = emailTextBody + addedCustomerRequestAppString + " to help manage your project.  When you approve, you'll see further instructions for securely sharing access to subscriptions that you already have.";
                    needDefaultMessage = false;
                  }

                  // New subscriptions (sign up)
                  if (addedSignUpString !== '') {
                    if (needDefaultMessage === true) {
                      emailTextBody = emailTextBody + "\n\nWe will sign up for ";
                    } else {
                      emailTextBody = emailTextBody + "\n\nWe will also sign up for ";
                    }
                    emailTextBody = emailTextBody + addedSignUpString + " to help manage your project.";
                    needDefaultMessage = false;
                  }

                  //
                  // --- Project wallet ---
                  //
                  //
                  if (customerPaySubscriptions === true) {
                    let autoFundingMessageAdded = false;
                    let alreadyHandledOneOff = false;
                    if (autoTopupAlreadyActive === false) {
                      if (autoTopupTurnedOn === true) {
                        if (subsequentSO === false) {
                          if (needDefaultMessage === true) {
                            if (addedCustomerPayAppString.length > 2) {
                              emailTextBody = emailTextBody + "\n\nWe'd like to set up and automatically fund a project wallet to pay for subscriptions that we'll purchase and manage for you (" + addedCustomerPayAppString + ").";
                            } else {
                              emailTextBody = emailTextBody + "\n\nWe'd like to set up and automatically fund a project wallet to pay for your subscriptions.";
                            }
                            needDefaultMessage = false;
                          } else {
                            emailTextBody = emailTextBody + "\n\nWe'd like to set up and automatically fund a project wallet to pay for your subscriptions.";
                          }
                          autoFundingMessageAdded = true; 
                        } else {
                          if (needDefaultMessage === true) {
                            if (addedCustomerPayAppString.length > 2) {
                              emailTextBody = emailTextBody + "\n\nWe'd like to automatically fund your project wallet to pay for subscriptions that we'll purchase and manage for you (" + addedCustomerPayAppString + ").";
                            } else {
                              emailTextBody = emailTextBody + "\n\nWe'd like to automatically fund your project wallet to pay for your subscriptions.";
                            }
                            needDefaultMessage = false;
                          } else {
                            emailTextBody = emailTextBody + "\n\nWe'd like to automatically fund your project wallet to pay for your subscriptions.";
                          }
                          autoFundingMessageAdded = true; 
                        }
                      } else {
                        // Auto-topup wasn't turned on, but is there a request for funds?
                        if (subsequentSO === false) {
                          if (customerFundsRequested === true) {
                            if (needDefaultMessage === true) {
                              if (addedCustomerPayAppString.length > 2) {
                                emailTextBody = emailTextBody + "\n\nWe'd like to set up a project wallet to pay for subscriptions that we'll purchase and manage for you (" + addedCustomerPayAppString + ").";
                              } else {
                                emailTextBody = emailTextBody + "\n\nWe'd like to set up a project wallet to pay for your subscriptions.";
                              }
                              needDefaultMessage = false;
                            } else {
                              emailTextBody = emailTextBody + "\n\nWe'd like to set up a project wallet to pay for your subscriptions.";
                            }
                            alreadyHandledOneOff = true;
                          }
                        } else {
                          // Subsequent SO
                          if (customerFundsRequested === true) {
                            if (needDefaultMessage === true) {
                              if (addedCustomerPayAppString.length > 2) {
                                emailTextBody = emailTextBody + "\n\nWe'd like to add funds to your project wallet to pay for subscriptions that we'll purchase and manage for you (" + addedCustomerPayAppString + ").";
                              } else {
                                emailTextBody = emailTextBody + "\n\nWe'd like to add funds to your project wallet to pay for your subscriptions.";
                              }
                              needDefaultMessage = false;
                            } else {
                              emailTextBody = emailTextBody + "\n\nWe'd like to add funds to your project wallet to pay for your subscriptions.";
                            }
                            alreadyHandledOneOff = true;
                          }
                        }
                      }
                    } else {
                      // Could be turning off or changing auto-topup
                      if (autoTopupTurnedOff === true) {
                        if (needDefaultMessage === true) {
                          emailTextBody = emailTextBody + "\n\nWe'd like to turn off automated funding for your project wallet that pays for your subscriptions.";
                          needDefaultMessage = false;
                        } else {
                          emailTextBody = emailTextBody + "\n\nWe'd like to turn off automated funding for your project wallet that pays for your subscriptions.";
                        }
                      } else if (autoTopupIncreased === true) {
                        if (needDefaultMessage === true) {
                          emailTextBody = emailTextBody + "\n\nWe'd like to increase the automated funding for your project wallet that pays for your subscriptions.";
                          needDefaultMessage = false;
                        } else {
                          if (addedCustomerPayAppString === '') {
                            emailTextBody = emailTextBody + "\n\nWe'd like to increase the automated funding for your project wallet that pays for your subscriptions.";
                          } else {
                            emailTextBody = emailTextBody + "\n\nWe'd like to increase the automated funding for your project wallet to accommodate the cost of new subscriptions (" + addedCustomerPayAppString + ")."; 
                          }
                        }
                        autoFundingMessageAdded = true; 
                      } else if (autoTopupDecreased === true) {
                        if (needDefaultMessage === true) {
                          emailTextBody = emailTextBody + "\n\nWe'd like to decrease the automated funding for your project wallet that pays for your subscriptions.";
                          needDefaultMessage = false;
                        } else {
                          emailTextBody = emailTextBody + "\n\nWe'd like to decrease the automated funding for your project wallet that pays for your subscriptions.";
                        }
                        autoFundingMessageAdded = true; 
                      }
                    }

                    // One-time funding
                    if (customerFundsRequested === true && alreadyHandledOneOff === false) {
                      if (autoFundingMessageAdded === true) {
                        emailTextBody = emailTextBody + "  We'd also like to add funds to your project wallet now.";
                      } else {
                        emailTextBody = emailTextBody + "\n\nWe'd like to add funds to your project wallet now.";
                      }

                      if (needDefaultMessage === true) {
                        needDefaultMessage = false;
                      }
                    } 

                    // Spend controls
                    if (limitChanged === true) {
                      let limitString = "\n\nWe'd like to";
                      if (limitAdded === true) {
                        limitString = limitString + " set a limit to";
                      } else if (limitRemoved === true) {
                        limitString = limitString + " remove the limit to";
                      } else if (limitIncreased === true) {
                        limitString = limitString + " increase the limit to";
                      } else if (limitDecreased === true) {
                        limitString = limitString + " decrease the limit to";
                      }

                      limitString = limitString + " the amount that can be spent on your subscriptions each month.";

                      if (needDefaultMessage === true) {
                        emailTextBody = emailTextBody + limitString;
                        needDefaultMessage = false;
                      } else {
                        emailTextBody = emailTextBody + limitString;
                      }
                    }
                  }

                  // Is there already a funding source on file?
                  let addedPaymentSourceVerbiage = false;
                  if (customerSourceOnFile === true && customerPaySubscriptions === true && autoTopupAlreadyActive === false) {
                    // This is a legacy edge case, in which a funding source is already on file but a budget has never
                    // been created.
                    emailTextBody = emailTextBody + "\n\nWe'll use your attached credit card or bank account to fund your project wallet.  Then we can automatically and transparently expense your subscriptions to you.";
                    addedPaymentSourceVerbiage = true;
                    if (needDefaultMessage === true) {
                      needDefaultMessage = false;
                    }
                  } else if (customerSourceOnFile === false && customerPaySubscriptions === true) {
                    emailTextBody = emailTextBody + "\n\nPlease use our client portal to securely link your credit card or bank account.  We'll use this to fund your project wallet.  Then we can automatically and transparently expense your subscriptions to you.";
                    addedPaymentSourceVerbiage = true;
                    if (needDefaultMessage === true) {
                      needDefaultMessage = false;
                    }
                  }

                  //
                  // -- Services --
                  //
                  if (servicesAdded === true) {
                    emailTextBody = emailTextBody + "\n\nWe have included our services for your project in this order.";
                    needDefaultMessage = false;

                    // Need to talk about payment here
                    if (customerSourceOnFile === false && addedPaymentSourceVerbiage === false) {
                      emailTextBody = emailTextBody + "  Please use our client portal to securely link your credit card or bank account.  When you approve, we'll bill our services to your linked credit card or account.";
                    } else {
                      emailTextBody = emailTextBody + "  When you approve, we'll bill our services to the credit card or bank account that funds your project wallet.";
                    }
                  }

                  //
                  // Default
                  //
                   if (needDefaultMessage === true) {
                    emailTextBody = emailTextBody + "\n\nWe'd like to manage subscriptions for your project.";
                  }

                  emailTextBody = emailTextBody + "\n\n- " + abEnvironment.getUserAttribute('a_accountName');

                  // Debug
                  if (debugString != null && debugString !== '') {
                    emailTextBody = emailTextBody + "\n\n" + debugString + "\n";
                  }

                }
              }

              this.textBodyDefault = emailTextBody;

              let sendButtonText = 'Send for approval';
              if (preApproveSubs === 1 && budgetChanged === false && servicesAdded === false) {
                sendButtonText = 'Send notification';
              }

              let parametersForActions = {
                pAccountId: theAccount.accountTByParentId.accountId,
                accountId: theAccount.accountId,
                accountName: theAccount.accountName,
                projectId: projectId,
                userToEmail: userToEmail,  // this is the user object
                emailSubject: emailSubject,
                preApproveSubs: preApproveSubs,
                rPreApproveSubs: rPreApproveSubs,
                budgetChanged: budgetChanged,
                servicesAdded: servicesAdded,
                budgetApprovedCount: budgetApprovedCount,
                customerPaidCount: customerPaidCount,
                autoTopupAmount: autoTopupAmount,
                initialBalanceAmount: initialBalanceAmount,
                rAutoTopupAmount: rAutoTopupAmount,
                rAutoTopupThreshold: rAutoTopupThreshold,
                rMaxMonthlyBudget: rMaxMonthlyBudget,
                rMaxCharge: rMaxCharge
              };

              return (
                <Grid container spacing={3} direction="row"  style={{paddingTop: '16px'}}>                  
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>  
                        <Paper>
                          <Grid container spacing={0} direction="row">
                            <Grid item xs={12} style={{textAlign: 'left', marginLeft: 16}}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitle}>{'Email to ' + theAccount.accountName}</Typography>  
                            </Grid>
                            <Grid item xs={12} className={classes.entryField}>
                              <Typography variant="subtitle1">
                                To: {userToEmail.email}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.entryField}>
                              <TextField style={{width: '90%'}}
                                         id="subject" label="Subject" 
                                         value={(this.state.emailSubject) ? this.state.emailSubject : emailSubject}
                                         onChange={ (e) => this.setState({ emailSubject: e.target.value }) } />
                            </Grid>
                
                            <Grid item xs={12}  className={classes.entryField}>
                              <TextField multiline={true}
                                         minRows={10}
                                         maxRows={20} 
                                         style={{width: '90%'}}
                                         id="body" label="Body" 
                                         value={this.getTextBodyForRender(emailTextBody)}
                                         onChange={ (e) => this.setState({ textBody: e.target.value }) } />
                            </Grid>

                            {(theAccount.accountTByParentId.accountStatus === 2) &&
                            <Grid item xs={12} style={{margin: 16}}>
                              <Paper elevation={1} style={AppBindStyles.tipPaper}>
                                <Grid container spacing={1} direction="row" justifyContent="flex-start">
                                  <Grid item xs={12}>
                                    <div style={{display:"flex"}}>
                                      <Typography variant="body1">
                                        <strong>Tip.</strong>&nbsp;&nbsp;Once you are verified and approved, you will be able to send this order to your customer.
                                      </Typography>
                                    </div>
                                  </Grid>      
                                </Grid>
                              </Paper>
                            </Grid>
                            }
                            <Grid item xs={12} align="center" style={{marginBottom: 16}}>
                              <Button disabled={this.isBackDisabled()}
                                      color="primary" 
                                      variant="outlined"
                                      onClick={() => this.props.handleBack()}
                                      style={AppBindStyles.bottomButton}>
                                Back
                              </Button>
                              {!this.showSpinner() && theAccount.accountTByParentId.accountStatus === 0 &&
                              <Button disabled={this.isSendEmailDisabled()} 
                                      color="primary"
                                      variant="contained" 
                                      onClick={() => {this.handleSend(parametersForActions)}} 
                                      style={AppBindStyles.bottomButton}>
                                {sendButtonText}
                              </Button> }
                              {this.showSpinner() && theAccount.accountTByParentId.accountStatus === 0 &&
                              <Button color="primary" 
                                      variant="contained" 
                                      style={AppBindStyles.bottomButton} >
                                <SavingSpinner/>
                              </Button> }
                              {!this.showSpinner() && theAccount.accountTByParentId.accountStatus !== 0 &&
                                <Button disabled={this.isSendEmailDisabled()} 
                                        color="primary"
                                        variant="contained" 
                                        onClick={() => {this.handleSaveAndVerify(theAccount.accountTByParentId.personaStatus)}} 
                                        style={AppBindStyles.bottomButton}>
                                  {theAccount.accountTByParentId.personaStatus ? 'Save' : 'Save and verify your account'}
                                </Button>
                              }
                              {preApproveSubs === 1 && budgetChanged === false && servicesAdded === false && !this.showSkipSpinner() &&
                                <Button color="primary"
                                        variant="outlined" 
                                        onClick={() => {this.handleSkip(parametersForActions)}} 
                                        style={AppBindStyles.bottomButton}>
                                  Skip notification
                                </Button> 
                                }
                              {preApproveSubs === 1 && budgetChanged === false && servicesAdded === false && this.showSkipSpinner() &&
                                <Button color="primary" 
                                        variant="outlined" 
                                        style={AppBindStyles.bottomButton}>
                                  <SavingSpinner/>
                                </Button>
                              }
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid> 
                  </Grid>
                </Grid>
              );
            } else {
              //display only - should never happen here.
              return ( 
                null          
              );
            }
          }
          if (!displayOnly) {
            return <div><LoadingSpinner/></div>
          }
        }
      }
      />
    );
  }
}


StepEmailTemplate.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(StepEmailTemplate);
